import Unsplash from 'unsplash-js';
import kebabCase from 'lodash.kebabcase';

import banzai from 'ub/control/banzai-features';
import planFeatures from 'ub/plan-features.js';
import { trackUserEvent } from 'ub/ui-user-tracking';

import score from 'ub/thirdparty/quicksilver';
import endpoints from 'ub/config/endpoints';
import { getType } from 'ui/asset-library/asset-types';
import { uploadFiles$ } from 'ub/control/asset-upload';

const handleResponse = response => {
  if (response.ok) {
    return response.json();
  } else {
    throw new Error(`${response.status} ${response.statusText}`);
  }
};

// Webapp

const mapWebappAssetToItem = asset => ({
  asset,
  filename: asset.content_file_name,
  fullUrl: asset.content_url,
  id: asset.uuid,
  thumbnailUrl: asset.content_url_small,
  type: getType(asset.content_content_type),
});

const fetchItemsFromClient = clientId =>
  fetch(endpoints.assetsByCompanyId(clientId), {
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
    },
  })
    .then(handleResponse)
    .then(assets => ({
      fetchedItems: assets.map(mapWebappAssetToItem),
      totalPages: 1, // The webapp does not currently support pagination of assets
    }));

const deleteItemsFromClient = (clientId, assetIntegerIds, authToken) =>
  fetch(endpoints.deleteAssets(clientId), {
    body: assetIntegerIds.map(assetId => `assets[]=${assetId}`).join('&'),
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-CSRF-Token': authToken,
    },
    method: 'POST',
  }).then(handleResponse);

const uploadFilesToClient = (clientId, files, authToken, onlyImages) =>
  uploadFiles$(files, authToken, onlyImages).map(result => {
    if (result.error) {
      return result;
    } else {
      return {
        error: null,
        item: mapWebappAssetToItem(result.asset),
      };
    }
  });

const filterByKeyword = (items, keyword) =>
  items
    .map(item => ({
      item,
      score: score(item.asset.content_file_name, keyword),
    }))
    .filter(({ score }) => score > 0)
    .map(({ item }) => item);

// Unsplash

const unsplash = new Unsplash({ applicationId: banzai.getFeatureValue('unsplashAccessKey') });

const UNSPLASH_PER_PAGE = 30; // Seems to be the maximum results that their API will return per page

const mapUnsplashPhotoToItem = photo => ({
  filename:
    `unsplash-${photo.id}` +
    (photo.slug || photo.description
      ? `-${(photo.slug || kebabCase(photo.description)).substr(0, 50)}`
      : '') +
    '.jpg',
  fullUrl: photo.urls.full, // photo.urls.raw is also available but the file sizes are huge
  id: photo.id,
  thumbnailUrl: photo.urls.small,
  type: 'images',
  unsplash: {
    authorLink: photo.user.links.html,
    authorName: photo.user.name,
    description: photo.description || '',
    link: photo.links.html,
  },
});

// Asset sources configuration

export default {
  currentClient: {
    deleteItems: (...args) => deleteItemsFromClient(window.editor.currentCompanyId, ...args),
    enabled: true,
    fetch: () => fetchItemsFromClient(window.editor.currentCompanyId),
    filterByKeyword,
    label: 'My Images',
    upload: (...args) => uploadFilesToClient(window.editor.currentCompanyId, ...args),
  },

  publicImages: {
    enabled: true,
    fetch: () => fetchItemsFromClient(window.editor.unbounceAssetsCompanyId),
    label: 'Unbounce Public Images',
    filterByKeyword,
  },

  unsplash: {
    enabled: planFeatures.isFeatureEnabled('unsplash'),
    fetch: page =>
      unsplash.photos
        .listPhotos(page, UNSPLASH_PER_PAGE, 'latest')
        .then(handleResponse)
        .then(photos => ({
          fetchedItems: photos.map(mapUnsplashPhotoToItem),
          // Unsplash doesn't give us a total count on this endpoint, so set an arbitrary amount
          // of pages to load
          totalPages: 5,
        })),
    fetchByKeyword: (keyword, page) =>
      unsplash.search
        .photos(keyword, page, UNSPLASH_PER_PAGE)
        .then(handleResponse)
        .then(response => ({
          fetchedItems: response.results.map(mapUnsplashPhotoToItem),
          totalPages: response.total_pages,
        })),
    isExternal: true,
    label: 'Unsplash Images',
    description:
      'Over 550,000 free (do-whatever-you-want) high-resolution photos brought to you by the ' +
      'world’s most generous community of photographers.',
    onChoose: item =>
      // A requirement of Unsplash's API is that we make a request to the download endpoint when the
      // user performs a download-like action. onChoose fires when the user selects the image to be
      // copied to their library. This is fire-and-forget – we don't care about the response or
      // whether it succeeded or failed.
      // https://medium.com/unsplash/unsplash-api-guidelines-triggering-a-download-c39b24e99e02
      unsplash.photos
        .getPhoto(item.id)
        .then(handleResponse)
        .then(unsplash.photos.downloadPhoto)
        .then(() => trackUserEvent('unsplash', 'addedImageToLibrary', { unsplashId: item.id }))
  },
};



// WEBPACK FOOTER //
// ./src/ui/asset-library/asset-sources.js